import React from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import ReactPlaceholder from 'react-placeholder/lib/ReactPlaceholder';
import classNames from 'clsx';
import AccountBalance from '@material-ui/icons/AccountBalance';
import Language from '@material-ui/icons/Language';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import LightbulbOn from 'mdi-material-ui/LightbulbOn';
import RadioboxMarked from 'mdi-material-ui/RadioboxMarked';
import { useSelector } from 'react-redux';

import { DictionaryEntry, DictionaryEntryKey } from 'app/services/dict.service';
import { ControlContainer } from 'app/components/ControlContainer';
import { RadioGroupControlProps } from './RadioGroupControl.interfaces';
import { InputPlaceholder } from 'app/components/InputPlaceholder';
import { TipMessage } from 'app/components/TipMessage';
import { useStyles } from './RadioGroupControl.styles';
import { getBulbMessage } from 'app/store/data/formsConfig';
import { RootState } from 'app/store/rootReducer';
import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import { useIsPersonalDataTwoAdvantagesView } from 'app/shared/hooks/useIsPersonalDataTwoAdvantagesView';

export default function RadioGroupControl(props: RadioGroupControlProps) {
  const classes = useStyles();

  const {
    input,
    isLoading,
    meta,
    label,
    subLabel,
    helperText,
    tooltip,
    width,
    formName,
    onValueChange,
    dictionary,
    onClick,
    className,
    questionKey,
  } = props;

  const bulbInfoMessage = useSelector((state: RootState) =>
    formName ? getBulbMessage(state, formName, questionKey) : undefined
  );
  const isPersonalDataTwoAdvantagesActive = useIsPersonalDataTwoAdvantagesView();

  const isPersonalDataQuestion =
    questionKey === FieldsEnum.PersonalDataPath &&
    formName === FormsEnum.CalculationForm &&
    isPersonalDataTwoAdvantagesActive;

  const handleChange = (value: string) => {
    const controlValue =
      value === 'true'
        ? true
        : value === 'false'
        ? false
        : typeof dictionary[0].key === 'number'
        ? parseInt(value)
        : value;

    if (input.value !== value) {
      input.onChange(controlValue);

      if (onValueChange) {
        onValueChange(controlValue, true);
      }
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const getLabelWithCustomIcon = (entry: DictionaryEntry) => {
    const Icon = getIcon(entry.key);
    if (Icon) {
      return (
        <div className={classes.iconWrapper}>
          {entry.value}
          <Icon className={classes.icon} />
        </div>
      );
    }

    return entry.subLabels ? getTitleWithSubtitle(entry) : entry.value;
  };

  const renderRadioButtons = () =>
    dictionary.map((entry) => renderRadioButton(entry));

  const getIcon = (key: DictionaryEntryKey) => {
    interface IconsArray {
      [key: string]: React.ComponentType<SvgIconProps>;
    }

    const icons: IconsArray = {
      online_transfer: Language,
      traditional_transfer: AccountBalance,
      internal_transfer: Language,
    };
    return icons[key.toString()];
  };

  const renderRadioButton = (entry: DictionaryEntry) => {
    const isActive = entry.key.toString() === input.value.toString();
    const disabled = !!entry.disabled;

    return (
      <FormControlLabel
        tabIndex={0}
        value={entry.key.toString()}
        key={entry.key.toString()}
        disabled={disabled}
        onKeyDown={(e) => {
          if (
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            e.preventDefault();
            handleChange(entry.key.toString());
          }
        }}
        control={
          <Radio
            onClick={handleClick}
            className={classes.radio}
            color="primary"
            disabled={disabled}
            checkedIcon={<RadioboxMarked />}
            inputProps={{
              tabIndex: -1,
            }}
          />
        }
        label={getLabelWithCustomIcon(entry)}
        className={classNames(
          classes.radioLabel,
          {
            [classes.radioLabelActive]: isActive,
            [classes.radioLabelWithMargin]: !entry.subLabels,
            [classes.disabled]: disabled,
            [classes.personalDataRadioLabelInline]: isPersonalDataQuestion,
          },
          className,
          'focusVisibleOutline'
        )}
        classes={{
          label: classNames({
            [classes.radioLabelActive]: isActive,
          }),
        }}
      />
    );
  };

  const getTitleWithSubtitle = (suggestion: DictionaryEntry) => {
    return (
      <>
        <span className={classes.menuItemTitle}>{suggestion.value}</span>
        <span className={classes.menuItemSubtitle}>
          {suggestion.subLabels &&
            suggestion.subLabels.map((subLabel) => (
              <span key={subLabel}>{subLabel}</span>
            ))}
        </span>
      </>
    );
  };

  return (
    <ReactPlaceholder
      ready={!isLoading}
      customPlaceholder={<InputPlaceholder width={width} />}
    >
      {meta.touched && !meta.valid && !isPersonalDataQuestion && (
        <div className={classes.error} />
      )}
      <ControlContainer
        inputId={input.name}
        withoutHtmlFor
        tooltip={tooltip}
        label={isPersonalDataQuestion ? '' : label}
        subLabel={subLabel}
        helperText={helperText}
        filled={!!input.value}
        width={width}
        meta={meta}
        tipMessage={
          bulbInfoMessage ? (
            <TipMessage
              content={bulbInfoMessage.content}
              icon={<LightbulbOn />}
            />
          ) : undefined
        }
      >
        <FormControl
          className={classes.fieldset}
          component={'fieldset' as 'div'}
        >
          <RadioGroup
            aria-labelledby={`${input.name}-label`}
            aria-label={label}
            name={input.name}
            value={input.value.toString()}
            className={classNames({
              [classes.personalDataRadioGroupRow]: isPersonalDataQuestion,
            })}
            onChange={(event, value) => handleChange(value)}
          >
            {renderRadioButtons()}
          </RadioGroup>
        </FormControl>
      </ControlContainer>
    </ReactPlaceholder>
  );
}
